import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LockIcon from '@mui/icons-material/LockOutlined';
import { StatusChip } from './StatusChip';

export interface ModuleTileProps {
  name: string;
  description: string;
  icon: ReactNode;
  onOpen: () => void;
  /**
   * Shown when the install has not licensed this module. Locked tiles stay visible
   * on purpose — the grid is an upsell surface and stays identical across clients.
   *
   * THIS is what turns a tile off. Nothing else does.
   */
  locked?: boolean;
  /**
   * The module is licensed but its screens have not been built yet.
   *
   * A LABEL ONLY. It adds a "Coming soon" chip and changes nothing else — the tile stays
   * full colour and stays clickable, because the client has paid for the module and it is
   * ON. Opening it lands on the module's "not built yet" page.
   */
  comingSoon?: boolean;
}

/**
 * A launcher tile on the portal start menu.
 *
 * Licensed-but-unbuilt modules appear disabled rather than hidden: a client should be
 * able to see what they have bought while the phased build lands, and an empty portal
 * would read as a broken install.
 *
 * ON or OFF, and the LICENCE is the only thing that decides which (owner's ruling,
 * 2026-08-03). "Not licensed" and "Coming soon" are both allowed to appear — they are
 * labels, and a reader is entitled to know a module is not built yet — but only
 * `locked` turns a tile off.
 *
 * It used to work the other way: an unbuilt module was greyed and disabled exactly like an
 * unlicensed one, so a module the owner had just switched on read as switched off, and an
 * entitlements bug was chased that did not exist.
 *
 * An unlicensed tile is greyed, carries a lock and a "Not licensed" chip, and does not
 * open. A licensed tile is full colour and opens, "Coming soon" chip or not.
 */
export function ModuleTile({
  name,
  description,
  icon,
  onOpen,
  locked,
  comingSoon,
}: ModuleTileProps) {
  const disabled = Boolean(locked);
  const suffix = locked ? '— not licensed' : comingSoon ? '— coming soon' : '';

  return (
    <Card
      sx={{
        height: '100%',
        // Greyed ONLY when unlicensed. A "Coming soon" module is ON and looks it.
        ...(locked ? { opacity: 0.72, backgroundColor: 'action.hover' } : {}),
      }}
    >
      <CardActionArea
        onClick={onOpen}
        disabled={disabled}
        sx={{ height: '100%', alignItems: 'stretch' }}
        aria-label={disabled ? `${name} ${suffix}` : `Open ${name}`}
      >
        <CardContent sx={{ height: '100%', p: 2.5 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              aria-hidden
              sx={{
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: 'action.selected',
                color: disabled ? 'text.disabled' : 'primary.main',
                '& svg': { fontSize: 26 },
              }}
            >
              {locked ? <LockIcon /> : icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  color={disabled ? 'text.disabled' : 'text.primary'}
                >
                  {name}
                </Typography>
                {locked ? <StatusChip tone="neutral" label="Not licensed" /> : null}
                {!locked && comingSoon ? <StatusChip tone="neutral" label="Coming soon" /> : null}
              </Stack>
              {/* A locked tile keeps its own description. The lock icon, the greyed
                  colour and the "Not licensed" chip already say it is unavailable —
                  replacing the text with a sales line as well told the reader three
                  times over that they could not have it, and nothing at all about what
                  it was. The whole reason these tiles are shown rather than hidden is so
                  a client can see what the platform offers. */}
              <Typography variant="body2" color={disabled ? 'text.disabled' : 'text.secondary'}>
                {description}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
